/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useMemo } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { StatefulTable, Table } from 'carbon-addons-iot-react';
import merge from 'lodash/merge';

import './CommonTable.module.css';

export const messages = defineMessages({
  selectAll: 'Select all',
  search: 'Search',
  selectRow: 'Select row',
  clickToExpand: 'Click to expand',
  clickToCollapse: 'Click to collapse',
  moreActions: 'More actions',
  previousPage: 'Previous page',
  nextPage: 'Next page',
  pageNumber: 'Page Number',
  itemsPerPage: 'Items per page:',
  itemsRange: '{min}–{max} items',
  currentPage: 'page {page}',
  itemsRangeWithTotal: '{min}–{max} of {total} items',
  pageRange: '{current} of {total} pages',
  clearAllFilters: 'Clear all filters',
  columnSelection: 'Column Selection',
  filters: 'Filters',
  clearFilter: 'Clear filter',
  filter: 'Filter',
  openMenu: 'Open menu',
  closeMenu: 'Close menu',
  clearSelection: 'Clear selection',
  emptyMessage: 'No data was found',
  emptyMessageWithFilters: 'No results match the current filters',
  emptyMessageBody: '',
  emptyMessageWithFiltersBody: 'Try another search or use column filter criteria',
  emptyButtonLabel: 'Create some data',
  emptyButtonLabelWithFilters: 'Clear all filters',
  inProgress: 'In progress',
  actionFailed: 'Action failed',
  learnMore: 'Learn more',
  dismiss: 'Dismiss',
  filterColumn: 'Filter column',
  cancel: 'Cancel',
  itemSelected: 'item selected',
  itemsSelected: 'items selected',
  filterNone: 'Unsort rows by this header',
  filterAscending: 'Sort rows by this header in ascending order',
  filterDescending: 'Sort rows by this header in descending order',
  // table error state
  tableErrorStateTitle: 'Unable to load the page',
  buttonLabelOnTableError: 'Refresh the page',
  editButtonAria: 'Edit rows',
  downloadIconDescription: 'Download table content',
});

/**
 *
 * @param {*} formatMessage formatter function
 * @param {*} actions list of available table actions
 */
export const createDefaultTableI18N = (formatMessage, actions) => ({
  /** pagination */
  pageBackwardAria: formatMessage(messages.previousPage),
  pageForwardAria: formatMessage(messages.nextPage),
  pageNumberAria: formatMessage(messages.pageNumber),
  itemsPerPage: formatMessage(messages.itemsPerPage),
  itemsRange: (min, max) => formatMessage(messages.itemsRange, { min, max }),
  currentPage: page => formatMessage(messages.currentPage, { page }),
  itemsRangeWithTotal: (min, max, total) =>
    formatMessage(messages.itemsRangeWithTotal, { min, max, total }),
  pageRange: (current, total) => formatMessage(messages.pageRange, { current, total }),
  /** table body */
  overflowMenuAria: formatMessage(messages.moreActions),
  clickToExpandAria: formatMessage(messages.clickToExpand),
  clickToCollapseAria: formatMessage(messages.clickToCollapse),
  selectAllAria: formatMessage(messages.selectAll),
  selectRowAria: formatMessage(messages.selectRow),
  /** toolbar */
  searchPlaceholder: formatMessage(messages.search),
  clearAllFilters: formatMessage(messages.clearAllFilters),
  columnSelectionButtonAria: formatMessage(messages.columnSelection),
  filterButtonAria: formatMessage(messages.filters),
  clearFilterAria: formatMessage(messages.clearFilter),
  filterAria: formatMessage(messages.filter),
  openMenuAria: formatMessage(messages.openMenu),
  closeMenuAria: formatMessage(messages.closeMenu),
  clearSelectionAria: formatMessage(messages.clearSelection),
  /** empty state */
  emptyMessage: formatMessage(messages.emptyMessage),
  emptyMessageWithFilters: formatMessage(messages.emptyMessageWithFilters),
  emptyButtonLabel: actions?.table?.onEmptyStateAction // eslint-disable-line react/prop-types
    ? formatMessage(messages.emptyButtonLabel)
    : undefined, // leave off button if no action is defined
  emptyButtonLabelWithFilters: formatMessage(messages.emptyButtonLabelWithFilters),
  /** Row actions test */
  inProgressText: formatMessage(messages.inProgress),
  actionFailedText: formatMessage(messages.actionFailed),
  learnMoreText: formatMessage(messages.learnMore),
  dismissText: formatMessage(messages.dismiss),
  /** selected actions */
  batchCancel: formatMessage(messages.cancel),
  itemSelected: formatMessage(messages.itemSelected),
  itemsSelected: formatMessage(messages.itemsSelected),
  filterNone: formatMessage(messages.filterNone),
  filterAscending: formatMessage(messages.filterAscending),
  filterDescending: formatMessage(messages.filterDescending),
  // table error state
  tableErrorStateTitle: formatMessage(messages.tableErrorStateTitle),
  buttonLabelOnTableError: formatMessage(messages.buttonLabelOnTableError),
  editButtonAria: formatMessage(messages.editButtonAria),
  downloadIconDescription: formatMessage(messages.downloadIconDescription),
});

/** This table is based on the prop model of the StatefulTable in https://carbon-addons-iot-react.com/, but adds i18n text and sets a few defaults, see the prop model documentation there for details  */
const CommonTable = ({
  i18n = {}, // eslint-disable-line react/prop-types
  options = {}, // eslint-disable-line react/prop-types
  columns = [], // eslint-disable-line react/prop-types
  actions = {}, // eslint-disable-line react/prop-types
  view = {}, // eslint-disable-line react/prop-types
  isAsync = false, // eslint-disable-line react/prop-types
  ...props
}) => {
  const { formatMessage, locale } = useIntl();
  const TableComponent = useMemo(() => (isAsync ? Table : StatefulTable), [isAsync]);
  return (
    <TableComponent
      testId={isAsync ? 'Table' : 'StatefulTable'}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      // eslint-disable-next-line react/prop-types
      columns={columns.map(column => ({
        ...column,
        filter:
          column.filter ||
          (column.isFilterable ? { placeholderText: formatMessage(messages.filterColumn) } : null),
      }))}
      // need to examine the onEmptyStateAction to determine the button labels
      actions={{
        ...actions,
        table: {
          ...actions?.table, // eslint-disable-line react/prop-types
          onEmptyStateAction: actions?.table?.onEmptyStateAction, // eslint-disable-line react/prop-types
        },
      }}
      options={
        options
          ? merge({}, options, {
              hasRowSelection: options.hasRowSelection === true ? 'multi' : options.hasRowSelection, // eslint-disable-line react/prop-types
              hasFilter: options.hasFilter === true ? 'onKeyPress' : options.hasFilter, // eslint-disable-line react/prop-types
            })
          : undefined
      }
      view={{
        ...view,
        toolbar: {
          ...view?.toolbar, // eslint-disable-line react/prop-types
          search: { ...view?.toolbar?.search, defaultExpanded: true, persistent: true }, // eslint-disable-line react/prop-types
        },
      }}
      locale={locale}
      i18n={merge({}, createDefaultTableI18N(formatMessage, actions), i18n)}
    />
  );
};

export default CommonTable;
